// API 서비스: zero1ne-marketing 백엔드와 통신
const API_BASE_URL = process.env.REACT_APP_CONTENT_API_URL || 'https://sslmo-dambi.com/api/v1';

export interface ContentGenerationRequest {
  category_id: number;
  keywords: string[];
  topic: string;
  tone?: string;
  length?: number;
  reference_content?: string;
  keyphrase?: string;
  include_images?: boolean;
}

export interface ImageInfo {
  src: string;
  alt?: string;
}

export interface GeneratedContent {
  id: number;
  title: string;
  content: string;
  keywords: string[];
  meta_description?: string;
  keyphrase?: string;
  images?: ImageInfo[];
  seo_score: number;
  readability_score?: number;
  status?: string;
  created_at: string;
  updated_at?: string;
  author_name?: string;
  author_email?: string;
  author_bio?: string;
  category: {
    id: number;
    name: string;
    description: string;
    created_at?: string;
    updated_at?: string;
  };
  category_id?: number;
  seo_details?: any;
  internal_links?: any[];
  headings?: string[];
  is_demo?: boolean;
}

export interface Category {
  id: number;
  name: string;
  description: string;
}

class ContentAPI {
  // 카테고리 목록 조회
  async getCategories(): Promise<Category[]> {
    const response = await fetch(`${API_BASE_URL}/categories`);
    if (!response.ok) {
      throw new Error('카테고리 조회 실패');
    }
    return response.json();
  }

  // AI 콘텐츠 생성
  async generateContent(request: ContentGenerationRequest): Promise<GeneratedContent> {
    console.log('API 요청 데이터:', request); // 디버깅용
    
    const requestBody = JSON.stringify(request);
    const requestSize = new Blob([requestBody]).size;
    console.log('요청 크기:', requestSize, 'bytes (', (requestSize/1024).toFixed(2), 'KB)'); // 디버깅용

    const response = await fetch(`${API_BASE_URL}/contents/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: requestBody,
    });
    
    console.log('API 응답 상태:', response.status, response.statusText); // 디버깅용
    
    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (e) {
        errorData = { detail: '응답을 파싱할 수 없습니다.' };
      }
      console.error('API 에러 응답:', {
        status: response.status,
        statusText: response.statusText,
        errorData
      }); // 디버깅용
      throw new Error(errorData.detail || '콘텐츠 생성 실패');
    }
    
    try {
      const responseText = await response.text();
      console.log('API 응답 텍스트:', responseText); // 디버깅용
      
      if (!responseText) {
        throw new Error('빈 응답을 받았습니다.');
      }
      
      const data = JSON.parse(responseText);
      console.log('파싱된 응답 데이터:', data); // 디버깅용
      return data;
    } catch (e) {
      console.error('JSON 파싱 오류:', e); // 디버깅용
      throw new Error(`응답 파싱 실패: ${e instanceof Error ? e.message : '알 수 없는 오류'}`);
    }
  }

  // 발행된 콘텐츠 목록 조회
  async getContents(): Promise<GeneratedContent[]> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10초 타임아웃
    
    try {
      const response = await fetch(`${API_BASE_URL}/contents/published`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`콘텐츠 조회 실패: ${response.status} ${response.statusText}`);
      }
      
      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('요청 시간이 초과되었습니다. 다시 시도해주세요.');
        }
        throw new Error(`콘텐츠 조회 실패: ${error.message}`);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  }

  // 모든 콘텐츠 목록 조회 (관리용)
  async getAllContents(): Promise<GeneratedContent[]> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    try {
      const response = await fetch(`${API_BASE_URL}/contents/`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`콘텐츠 조회 실패: ${response.status} ${response.statusText}`);
      }
      
      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('요청 시간이 초과되었습니다. 다시 시도해주세요.');
        }
        throw new Error(`콘텐츠 조회 실패: ${error.message}`);
      }
      throw new Error('알 수 없는 오류가 발생했습니다.');
    }
  }

  // 특정 콘텐츠 조회
  async getContent(id: number): Promise<GeneratedContent> {
    const response = await fetch(`${API_BASE_URL}/contents/${id}`);
    if (!response.ok) {
      throw new Error('콘텐츠 조회 실패');
    }
    return response.json();
  }

  // 콘텐츠 분석 (기존 글 기반)
  async analyzeContent(content: string, title: string) {
    const response = await fetch(`${API_BASE_URL}/contents/analyze-content`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ content, title }),
    });
    
    if (!response.ok) {
      throw new Error('콘텐츠 분석 실패');
    }
    
    return response.json();
  }

  // 톤앤매너별 변형 생성
  async generateToneVariations(content: string, title: string, categoryId: number, selectedTones?: string[]) {
    const response = await fetch(`${API_BASE_URL}/contents/generate-tone-variations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        content,
        title,
        category_id: categoryId,
        selected_tones: selectedTones,
        include_images: true,
      }),
    });
    
    if (!response.ok) {
      throw new Error('톤앤매너 변형 생성 실패');
    }
    
    return response.json();
  }

  // 콘텐츠 수정
  async updateContent(id: number, updateData: Partial<GeneratedContent>): Promise<GeneratedContent> {
    const response = await fetch(`${API_BASE_URL}/contents/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '콘텐츠 수정 실패');
    }
    
    return response.json();
  }

  // 콘텐츠 삭제
  async deleteContent(id: number): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/contents/${id}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '콘텐츠 삭제 실패');
    }
  }

  // AI 수정 요청
  async requestEdit(editRequest: {
    content: string;
    context: string;
    feedback: string;
    section: string;
  }): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/contents/edit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(editRequest),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || 'AI 수정 요청 실패');
    }
    
    const result = await response.json();
    return result.edited_content;
  }

  // 수동 콘텐츠 생성
  async createManualContent(data: {
    title: string;
    content: string;
    category_id: number;
    author_name?: string;
    author_email?: string;
  }): Promise<GeneratedContent> {
    const requestBody = JSON.stringify(data);
    const requestSize = new Blob([requestBody]).size;
    console.log('수동 콘텐츠 생성 요청 크기:', requestSize, 'bytes (', (requestSize/1024).toFixed(2), 'KB)');

    const response = await fetch(`${API_BASE_URL}/contents/manual`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: requestBody,
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || '수동 콘텐츠 생성 실패');
    }
    
    return response.json();
  }
}

export const contentAPI = new ContentAPI(); 